// src/components/manager/PriorityQueue.jsx
// Enhanced Priority Queue - Kanban, Calendar, Today - FLAT ICONS ONLY
// Integrates Strategic Initiatives: Add-to-Queue pending + initiative milestones/update-required
// Supports Assignment System: Assign tasks to team members

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Paperclip } from 'lucide-react';
import { InitiativesDB } from '../../utils/initiativesData';
import AssignmentModal from '../assignments/AssignmentModal';
import { mockUser } from '../../utils/mockData';
import { Button, KPICard } from '../ui';

// Flat Icon Component
function FlatIcon({ type, size = 20 }) {
  const icons = {
    today: { text: 'TD', color: '#EF4444' },
    scheduled: { text: 'SC', color: '#F59E0B' },
    backlog: { text: 'BL', color: '#6B7280' },
    parking: { text: 'PK', color: '#9CA3AF' },
    calendar: { text: 'CAL', color: '#2C5FA8' },
    kanban: { text: 'KB', color: '#00CED1' },
    list: { text: 'LS', color: '#10B981' },
    high: { text: 'H', color: '#EF4444' },
    medium: { text: 'M', color: '#F59E0B' },
    low: { text: 'L', color: '#10B981' }
  };

  const icon = icons[type] || icons.today;

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '4px',
      background: icon.color,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.35,
      fontWeight: '600',
      letterSpacing: '-0.3px',
      flexShrink: 0
    }}>
      {icon.text}
    </div>
  );
}

const DEFAULT_PRIORITIES = [
    {
      id: 1,
      title: 'Review staffing gaps for weekend coverage',
      status: 'today',
      priority: 'high',
      dueDate: '2026-02-07',
      category: 'Staffing',
      notes: 'Two RNs called out sick',
      scheduledDate: '2026-02-07'
    },
    {
      id: 2,
      title: 'Complete Q1 quality metrics report',
      status: 'today',
      priority: 'high',
      dueDate: '2026-02-07',
      category: 'Quality',
      notes: 'Due to director by EOD',
      scheduledDate: '2026-02-07'
    },
    {
      id: 3,
      title: 'Address patient wait time increase',
      status: 'today',
      priority: 'medium',
      dueDate: '2026-02-07',
      category: 'Access',
      notes: 'Average wait: 28 minutes',
      scheduledDate: '2026-02-07'
    },
    {
      id: 4,
      title: 'Review budget variance for January',
      status: 'scheduled',
      priority: 'medium',
      dueDate: '2026-02-10',
      category: 'Finance',
      notes: 'Finance review meeting',
      scheduledDate: '2026-02-10'
    },
    {
      id: 5,
      title: 'Update clinical protocols documentation',
      status: 'backlog',
      priority: 'low',
      dueDate: '2026-02-15',
      category: 'Quality',
      scheduledDate: null
    },
    {
      id: 6,
      title: 'Evaluate new scheduling software demo',
      status: 'parking',
      priority: 'low',
      dueDate: null,
      category: 'Initiatives',
      scheduledDate: null
    }
  ];

function PriorityQueue({ unitId }) {
  const [viewMode, setViewMode] = useState('today');
  const [showAddModal, setShowAddModal] = useState(false);
  const [priorities, setPriorities] = useState(DEFAULT_PRIORITIES);
  const [assignmentItem, setAssignmentItem] = useState(null);

  useEffect(() => {
    const pending = InitiativesDB.getAddToQueuePending();
    InitiativesDB.clearAddToQueuePending();
    const initiativeEnabled = InitiativesDB.isFeatureEnabled('manager');
    const initiativeItems = initiativeEnabled ? InitiativesDB.getInitiativeQueueItems() : [];

    if (pending.length === 0 && initiativeItems.length === 0) return;

    setPriorities(prev => {
      const existingIds = new Set(prev.map(p => String(p.id)));
      const addFromPending = pending.map(p => ({
        id: `add-${p.initiativeId}-${p.addedAt || Date.now()}`,
        title: `Follow up: ${p.initiativeName}`,
        status: 'backlog',
        priority: 'medium',
        dueDate: null,
        category: 'Initiatives',
        notes: `From Strategic Initiative: ${p.initiativeName}`,
        scheduledDate: null,
        initiativeId: p.initiativeId,
        initiativeName: p.initiativeName,
        type: 'initiative-follow-up'
      })).filter(item => !existingIds.has(String(item.id)));
      addFromPending.forEach(item => existingIds.add(String(item.id)));

      const addFromInitiatives = initiativeItems.filter(item => !existingIds.has(String(item.id)));
      return [...prev, ...addFromPending, ...addFromInitiatives];
    });
  }, []);

  const viewButtons = [
    { id: 'today', label: "Today's Priorities", iconType: 'today' },
    { id: 'kanban', label: 'Kanban Board', iconType: 'kanban' },
    { id: 'calendar', label: 'Calendar View', iconType: 'calendar' }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Priority Queue</h1>
          <p className="dashboard-subtitle">Manage and schedule your priorities</p>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setShowAddModal(true)}
        >
          + Add Priority
        </Button>
      </div>

      <div className="flex gap-1 border-b border-nn-border mb-4">
        {viewButtons.map(view => (
          <div
            key={view.id}
            className={viewMode === view.id
              ? 'flex items-center gap-2 px-3 py-2 text-[13px] font-medium rounded-t text-aqua border-b-2 border-aqua'
              : 'flex items-center gap-2 px-3 py-2 text-[13px] font-medium text-text-dark-secondary hover:text-text-dark transition-colors'}
            onClick={() => setViewMode(view.id)}
            onKeyDown={(e) => e.key === 'Enter' && setViewMode(view.id)}
            role="button"
            tabIndex={0}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FlatIcon type={view.iconType} size={16} />
              <span>{view.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '24px' }}>
        {viewMode === 'today' && <TodayView priorities={priorities} setPriorities={setPriorities} onAssign={(item) => setAssignmentItem(item)} />}
        {viewMode === 'kanban' && <KanbanView priorities={priorities} setPriorities={setPriorities} onAssign={(item) => setAssignmentItem(item)} />}
        {viewMode === 'calendar' && <CalendarView priorities={priorities} setPriorities={setPriorities} />}
      </div>

      {showAddModal && (
        <AddPriorityModal
          onClose={() => setShowAddModal(false)}
          onAdd={(newPriority) => {
            const nextId = Math.max(...priorities.map(p => p.id), 0) + 1;
            setPriorities([...priorities, { ...newPriority, id: nextId, dueDate: newPriority.dueDate || newPriority.scheduledDate }]);
            setShowAddModal(false);
          }}
        />
      )}

      {assignmentItem && (
        <AssignmentModal
          item={assignmentItem}
          currentUserId={mockUser.id}
          currentUserName={mockUser.name}
          currentUserRole="manager"
          onClose={() => setAssignmentItem(null)}
          onAssign={() => {
            setAssignmentItem(null);
            // Optionally refresh priorities or show success message
          }}
        />
      )}
    </div>
  );
}

// Today's Priorities View
function TodayView({ priorities, setPriorities, onAssign }) {
  const todayPriorities = priorities.filter(p => p.status === 'today');

  const priorityGroups = {
    high: todayPriorities.filter(p => p.priority === 'high'),
    medium: todayPriorities.filter(p => p.priority === 'medium'),
    low: todayPriorities.filter(p => p.priority === 'low')
  };

  const moveToDone = (id) => {
    setPriorities(priorities.map(p =>
      p.id === id ? { ...p, status: 'done' } : p
    ));
  };

  return (
    <div>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
        <KPICard size="md" density="compact" label="High Priority" value={priorityGroups.high.length} accentColor="var(--red-critical)" alertLevel="critical" subLabel="Requires immediate attention" />
        <KPICard size="md" density="compact" label="Medium Priority" value={priorityGroups.medium.length} accentColor="var(--yellow)" alertLevel="warning" subLabel="Complete today" />
        <KPICard size="md" density="compact" label="Low Priority" value={priorityGroups.low.length} accentColor="var(--royal)" subLabel="As time permits" />
        <KPICard size="md" density="compact" label="Total Today" value={todayPriorities.length} accentColor="var(--aqua)" subLabel="In your queue" />
      </div>

      {['high', 'medium', 'low'].map(priority => (
        <div key={priority} style={{ marginTop: '32px' }}>
          <div className="flex items-center justify-between mb-4">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FlatIcon type={priority} size={24} />
              <h3 className="text-[15px] font-semibold text-text-dark" style={{ textTransform: 'capitalize' }}>
                {priority} Priority ({priorityGroups[priority].length})
              </h3>
            </div>
          </div>

          {priorityGroups[priority].length === 0 ? (
            <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative">
              <div className="p-5">
                <div style={{ textAlign: 'center', padding: '20px' }} className="text-text-dark-secondary">
                  No {priority} priority items for today
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {priorityGroups[priority].map(item => (
                <PriorityCard
                  key={item.id}
                  item={item}
                  onComplete={() => moveToDone(item.id)}
                  onAssign={() => onAssign?.(item)}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Kanban Board View
function KanbanView({ priorities, setPriorities, onAssign }) {
  const columns = [
    { id: 'today', label: 'Today', iconType: 'today' },
    { id: 'scheduled', label: 'Scheduled', iconType: 'scheduled' },
    { id: 'backlog', label: 'Backlog', iconType: 'backlog' },
    { id: 'parking', label: 'Parking Lot', iconType: 'parking' }
  ];

  const moveCard = (itemId, newStatus) => {
    setPriorities(priorities.map(p =>
      p.id === itemId ? { ...p, status: newStatus } : p
    ));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-semibold text-text-dark">Kanban Board</h2>
        <p className="text-[13px] text-text-dark-secondary mt-0.5">Organize and manage priorities across different stages</p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginTop: '24px'
      }}>
        {columns.map(column => (
          <div key={column.id} className="bg-surface-warm rounded-lg p-4 min-h-[500px]">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #E5E7EB'
            }}>
              <FlatIcon type={column.iconType} size={20} />
              <div>
                <div className="text-[14px] font-semibold text-text-dark">
                  {column.label}
                </div>
                <div className="text-text-dark-secondary text-body-sm">
                  {priorities.filter(p => p.status === column.id).length} items
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {priorities
                .filter(p => p.status === column.id)
                .map(item => (
                  <KanbanCard
                    key={item.id}
                    item={item}
                    onMove={(newStatus) => moveCard(item.id, newStatus)}
                    columns={columns}
                    onAssign={() => onAssign?.(item)}
                  />
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Calendar View
function CalendarView({ priorities }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getPrioritiesForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return priorities.filter(p => (p.scheduledDate || p.dueDate) === dateStr);
  };

  const selectedDatePriorities = getPrioritiesForDate(selectedDate);

  const goToPreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentMonth(newDate);
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-semibold text-text-dark">Calendar View</h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={goToPreviousMonth}
          >
            ← Previous
          </Button>
          <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '150px', textAlign: 'center' }}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={goToNextMonth}
          >
            Next →
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginTop: '24px' }}>
        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative">
          <div className="p-5" style={{ padding: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6B7280',
                  padding: '8px 0'
                }}>
                  {day}
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
              {days.map((date, idx) => {
                const isToday = date && date.toDateString() === new Date().toDateString();
                const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
                const datePriorities = date ? getPrioritiesForDate(date) : [];

                return (
                  <div
                    key={idx}
                    onClick={() => date && setSelectedDate(date)}
                    onKeyDown={(e) => date && e.key === 'Enter' && setSelectedDate(date)}
                    role={date ? 'button' : undefined}
                    tabIndex={date ? 0 : undefined}
                    style={{
                      minHeight: '80px',
                      padding: '8px',
                      background: isSelected ? '#EFF6FF' : date ? 'white' : '#F9FAFB',
                      border: isToday ? '2px solid #2C5FA8' : '1px solid #E5E7EB',
                      borderRadius: '6px',
                      cursor: date ? 'pointer' : 'default',
                      transition: 'background 0.2s'
                    }}
                  >
                    {date && (
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: isToday ? '700' : '500',
                          color: isToday ? '#2C5FA8' : '#1a1a1a',
                          marginBottom: '4px'
                        }}>
                          {date.getDate()}
                        </div>
                        {datePriorities.length > 0 && (
                          <div style={{ fontSize: '10px', color: '#6B7280' }}>
                            {datePriorities.length} {datePriorities.length === 1 ? 'item' : 'items'}
                          </div>
                        )}
                        {datePriorities.slice(0, 2).map((p, i) => (
                          <div
                            key={i}
                            style={{
                              fontSize: '10px',
                              padding: '2px 4px',
                              background: p.priority === 'high' ? '#FEE2E2' : p.priority === 'medium' ? '#FEF3C7' : '#E8F5E9',
                              borderRadius: '3px',
                              marginTop: '2px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {p.title}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative">
          <div className="px-5 py-3 border-b border-nn-border">
            <div className="text-[14px] font-semibold text-text-dark">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div className="p-5">
            {selectedDatePriorities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }} className="text-text-dark-secondary">
                <FlatIcon type="calendar" size={48} />
                <div className="text-body">No priorities scheduled for this date</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {selectedDatePriorities.map(item => (
                  <div key={item.id} style={{
                    padding: '12px',
                    background: '#F9FAFB',
                    borderRadius: '6px',
                    borderLeft: `4px solid ${item.priority === 'high' ? '#EF4444' : item.priority === 'medium' ? '#F59E0B' : '#10B981'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      <FlatIcon type={item.priority} size={16} />
                      <div style={{ flex: 1 }}>
                        <div className="text-body" style={{ fontWeight: '500', color: '#1a1a1a', marginBottom: '4px' }}>
                          {item.title}
                        </div>
                        <div className="text-text-dark-secondary text-caption">
                          {item.category}
                        </div>
                      </div>
                    </div>
                    {item.notes && (
                      <div className="text-text-dark-secondary text-body-sm" style={{ marginTop: '8px' }}>
                        {item.notes}
                      </div>
                    )}
                    {(item.initiativeId || item.type?.startsWith('initiative')) && (
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #E5E7EB' }}>
                        <Link to="/initiatives" className="text-body-sm" style={{ fontWeight: '600', color: '#2C5FA8' }}>
                          View initiative →
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Priority Card
function PriorityCard({ item, onComplete, onAssign }) {
  const priorityColors = {
    high: '#EF4444',
    medium: '#F59E0B',
    low: '#10B981'
  };

  return (
    <div className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative" style={{ borderLeft: `4px solid ${priorityColors[item.priority]}` }}>
      <div className="p-5">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FlatIcon type={item.priority} size={20} />
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '500', color: '#1a1a1a' }}>
                {item.title}
              </h4>
            </div>

            <div className="text-text-dark-secondary text-body-sm" style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
              <span><strong>Category:</strong> {item.category}</span>
              <span><strong>Due:</strong> {item.dueDate}</span>
            </div>

            {item.notes && (
              <div className="text-text-dark-secondary text-body" style={{ marginTop: '8px', padding: '8px', background: '#F9FAFB', borderRadius: '4px' }}>
                {item.notes}
              </div>
            )}
            {(item.initiativeId || item.type?.startsWith('initiative')) && (
              <div style={{ marginTop: '8px' }}>
                <Link to="/initiatives" className="text-body-sm" style={{ fontWeight: '600', color: '#2C5FA8' }}>
                  View initiative →
                </Link>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Button type="button" variant="default" size="sm" onClick={onComplete}>
            ✓ Complete
          </Button>
          {onAssign && (
            <Button type="button" variant="default" size="sm" onClick={onAssign} title="Assign this task to someone">
              <Paperclip size={14} strokeWidth={2} /> Assign
            </Button>
          )}
          <Button type="button" variant="default" size="sm">
            Reschedule
          </Button>
        </div>
      </div>
    </div>
  );
}

// Kanban Card
function KanbanCard({ item, onMove, columns, onAssign }) {
  const [showMenu, setShowMenu] = useState(false);

  const priorityColors = {
    high: '#EF4444',
    medium: '#F59E0B',
    low: '#10B981'
  };

  return (
    <div
      className="bg-nn-white rounded-card-lg shadow-card overflow-hidden relative"
      style={{
        cursor: 'move',
        borderLeft: `4px solid ${priorityColors[item.priority]}`,
        position: 'relative'
      }}
    >
      <div className="p-5" style={{ padding: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
          <FlatIcon type={item.priority} size={16} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="text-body" style={{ fontWeight: '500', color: '#1a1a1a', marginBottom: '4px' }}>
              {item.title}
            </div>
            <div className="text-text-dark-secondary text-caption">
              {item.category}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowMenu(!showMenu)}
            style={{ fontSize: '16px', color: '#6B7280' }}
            className="h-auto w-auto p-[4px]"
            aria-label="Move to column"
          >
            ⋮
          </Button>
        </div>

        {item.dueDate && (
          <div className="text-text-dark-secondary text-caption" style={{ marginTop: '8px' }}>
            Due: {item.dueDate}
          </div>
        )}

        {showMenu && (
          <div>
            <div style={{
              position: 'absolute',
              top: '40px',
              right: '12px',
              background: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 11,
              minWidth: '150px'
            }}>
              {onAssign && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    onAssign();
                    setShowMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontSize: '12px',
                    color: '#374151',
                    borderBottom: '1px solid #E5E7EB'
                  }}
                  className="h-auto rounded-none justify-start"
                >
                  <Paperclip size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Assign
                </Button>
              )}
              <div className="text-caption" style={{ padding: '8px', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#6B7280' }}>
                Move to:
              </div>
              {columns.filter(c => c.id !== item.status).map(col => (
                <Button
                  key={col.id}
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    onMove(col.id);
                    setShowMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    textAlign: 'left',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  className="h-auto rounded-none justify-start"
                >
                  <FlatIcon type={col.iconType} size={14} />
                  {col.label}
                </Button>
              ))}
            </div>
            <div
              role="button"
              tabIndex={-1}
              onClick={() => setShowMenu(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowMenu(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10
              }}
              aria-label="Close menu"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Add Priority Modal
function AddPriorityModal({ onClose, onAdd }) {
  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    category: 'Quality',
    status: 'today',
    dueDate: new Date().toISOString().split('T')[0],
    scheduledDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd({ ...formData, dueDate: formData.dueDate || formData.scheduledDate });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-nn-white rounded-card-lg shadow-md w-full max-w-lg" style={{ maxHeight: '90vh', overflow: 'auto' }}>
        <div className="flex items-center justify-between mb-5" style={{ padding: '24px', borderBottom: '1px solid #E5E7EB', marginBottom: 0 }}>
          <h2 className="text-[18px] font-bold text-text-dark">Add New Priority</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            style={{ fontSize: '24px', color: '#6B7280' }}
            className="h-auto w-auto p-0"
            aria-label="Close"
          >
            ×
          </Button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
              Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              placeholder="What needs to be done?"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="mb-4">
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              >
                <option value="Access">Access</option>
                <option value="Quality">Quality</option>
                <option value="Staffing">Staffing</option>
                <option value="Finance">Finance</option>
                <option value="Initiatives">Initiatives</option>
                <option value="Consumer Insights">Consumer Insights</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }} className="mb-4">
            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              >
                <option value="today">Today</option>
                <option value="scheduled">Scheduled</option>
                <option value="backlog">Backlog</option>
                <option value="parking">Parking Lot</option>
              </select>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
                Scheduled Date
              </label>
              <input
                type="date"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                className="w-full h-9 rounded-lg border border-nn-border bg-nn-white px-3 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-[13px] font-medium text-text-dark-secondary mb-1.5">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-lg border border-nn-border bg-nn-white px-3 py-2 text-[13px] text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/30"
              style={{ boxSizing: 'border-box', fontFamily: 'inherit' }}
              placeholder="Additional context or details..."
            />
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-nn-border">
            <Button variant="default" size="sm" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" type="submit">
              Add Priority
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PriorityQueue;
